import React from "react";
import { Groups, venues } from "@fedsim/constants";
import { getStagesRemainingCount, getStorylineTitle } from "@fedsim/storylines";
import { differenceInDays, numberFormatCurrency } from "@fedsim/helpers";
import { Stack, Box, Button } from "@mui/material";
import LocationOnIcon from '@mui/icons-material/LocationOn';
import {
  Completion,
  DateDisplay,
  Duration,
  SegmentCost,
  SegmentType,
  Caption,
  SPACING,
  CostRow,
} from "../../typography";
import { NameBarStack, NameBar, AvatarStack } from "../../namebar";
import { styled } from '@mui/material/styles'

import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import BookerIcon from '@mui/icons-material/MenuBook';
import CompleteIcon from '@mui/icons-material/CheckBox'
import InCompleteIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CalendarMonth from "@mui/icons-material/CalendarMonth";
import NumbersIcon from "@mui/icons-material/Numbers";
import FilterListIcon from "@mui/icons-material/FilterList";
import CostIcon from '@mui/icons-material/MonetizationOn'

export function FeudStack(props) {
  const {
    brands = [],
    startDate = null,
    complete = false,
    championships = [],
    wrestlers = [],
    stageId = null,
    storylineId = null
  } = props;
  const names = wrestlers.map((item) => item.name);
  const startDateUI = getUIDate('Started', startDate)

  return (
    <>
      <AvatarStack collection={brands.concat(championships)} />
      {names && (
        <Stack direction={{ xs: "column", lg: "row" }}
          spacing={SPACING}>
          <Stack direction="row"
            width="100%">
            {names.map((name, index) => (
              <React.Fragment key={name}>
                <b>{name}</b>
                {names.length - 1 !== index ? <i>&nbsp; VS &nbsp;</i> : null}
              </React.Fragment>
            ))}
          </Stack>
        </Stack>
      )}
      <div>
        Stage {stageId} of {getStagesRemainingCount({ stageId, storylineId })}
      </div>
      <div>{getStorylineTitle({ stageId, storylineId })}</div>
      <Stack spacing={SPACING}
        direction="column">
        {startDate && <Stack
          minWidth={220}
          alignItems="center"
          justifyContent="space-between"
          direction="row">
          <CalendarMonthIcon />
          <Caption>
            {startDateUI}
          </Caption>
        </Stack>}
        <Stack alignItems="center"
          justifyContent="space-between"
          direction="row">
          {complete ? <CompleteIcon /> : <InCompleteIcon />}
          <Caption>
            {complete ? "Completed" : "In Progress"}
          </Caption>
        </Stack>
      </Stack>
    </>
  );
}


function getUIDate(prefix, date) {
  return `${prefix && `${prefix}: `}${new Date(date).toDateString()}`
}

export function FactionStack(props) {
  const {
    wrestlers = [],
    managers = [],
    leaders = [],
    brands = [],
    startDate = new Date(),
    endDate = false
  } = props;
  const startDateUI = getUIDate('Started', startDate)
  const endDateUI = endDate ? getUIDate('Ended', endDate) : false

  return (
    <>
      <Stack direction="row">
        {startDate && <Stack
          minWidth={220}
          alignItems="center"
          justifyContent="space-between"
          direction="row">
          <CalendarMonthIcon />
          <Caption>
            {startDateUI}
          </Caption>
        </Stack>}
        {endDate && <Stack
          minWidth={220}
          alignItems="center"
          justifyContent="space-between"
          direction="row">
          <CalendarMonthIcon />
          <Caption>{endDateUI}</Caption>
        </Stack>}
      </Stack>
      <AvatarStack collection={leaders.concat(wrestlers).concat(managers)} />
      {brands && <AvatarStack collection={brands} />}
    </>
  );
}
export function BrandStack({ director = null, balance = 0 }) {
  const balanceui = numberFormatCurrency(balance)

  return (
    <>
      {director?.name && (
        <>
          <AvatarStack collection={[director]} />
          <Stack
            spacing={0}
            minWidth={220}
            alignItems="center"
            justifyContent="space-between"
            direction="row">
            <BookerIcon />
            <Caption>{director.name}</Caption>
          </Stack>
          {balance && <Stack
            minWidth={180}
            alignItems="center"
            justifyContent="space-between"
            direction="row">
            <CostIcon />
            <Caption>{balanceui}</Caption>
          </Stack>}
        </>
      )}
    </>
  );
}

export function DraftStack(props) {
  const {
    cost,
    date = null,
    complete = null,
    brand = { name: "Vacant", image: false },
    wrestler = { name: "Vacant", image: false }
  } = props;
  return (
    <>
      <NameBar {...wrestler} />
      <NameBar {...brand} />
      {date && <DateDisplay>{date}</DateDisplay>}
      <CostRow label="Cost"
        value={cost} />
      <Completion value={complete} />
    </>
  );
}

export function ReignStack(props) {
  const { wrestlers, championship } = props;
  const startDate = new Date(props?.startDate);
  const endDate = props?.endDate ? new Date(props?.endDate) : false;
  const lengthOfReign = endDate ? differenceInDays(endDate, startDate) : false;
  const labelLength = !endDate ? "Ongoing" : `${lengthOfReign} days`;

  const startDateUI = getUIDate('Started', startDate)
  const endDateUI = endDate ? getUIDate('Ended', endDate) : false

  return (
    <>
      <AvatarStack collection={wrestlers?.concat([championship])} />
      <Stack spacing={1}>
        <Stack
          direction="column"
          spacing={SPACING}>
          {startDate && <Stack
            minWidth={220}
            alignItems="center"
            justifyContent="space-between"
            direction="row">
            <CalendarMonthIcon />
            <Caption>
              {startDateUI}
            </Caption>
          </Stack>}
          {endDate && <Stack
            minWidth={220}
            alignItems="center"
            justifyContent="space-between"
            direction="row">
            <CalendarMonthIcon />
            <Caption>{endDateUI}</Caption>
          </Stack>}
        </Stack>
        <Stack
          minWidth={220}
          alignItems="center"
          justifyContent="space-between"
          direction="row">
          <NumbersIcon /><Caption>{labelLength}</Caption>
        </Stack>
      </Stack>
    </>
  );
}

export function ProductionStack(props) {
  const {
    brands = [],
    segmentIds = [],
    venueId = 'DEFAULT',
    cost = false,
    date = new Date(),
    complete = false
  } = props;
  const venue = venues?.find((item) => item.id === venueId);
  return (
    <Stack
      spacing={SPACING}>
      <AvatarStack collection={brands} />
      <Stack minWidth={220} alignItems="center"
        justifyContent="space-between"
        direction="row">
        <NumbersIcon /> <Caption>{segmentIds?.length} segments</Caption>
      </Stack>
      <Stack alignItems="center"
        justifyContent="space-between"
        direction="row">
        <CalendarMonth />
        <Caption>
          {new Date(date).toDateString()}
        </Caption>
      </Stack>
      {venue && (
        <Stack alignItems="center"
          justifyContent="space-between"
          direction="row">
          <LocationOnIcon />
          <Caption>{venue.name}</Caption>
        </Stack>)
      }
      <Stack alignItems="center"
        justifyContent="space-between"
        direction="row">
        {complete ? <CompleteIcon /> : <InCompleteIcon />}
        <Caption>
          {complete ? "Completed" : "In Progress"}
        </Caption>
      </Stack>
      {cost && <CostRow label="Cost"
        value={cost} />}
    </Stack>
  );
}

export function SegmentStack(props) {
  const {
    date = null,
    duration = 0,
    complete = false,
    type = "",
    championships = []
  } = props;
  return (
    <>
      <NameBarStack collection={championships} />
      <SegmentCost>{type}</SegmentCost>
      <SegmentType>{type}</SegmentType>
      <Duration value={duration} />
      <DateDisplay>{date}</DateDisplay>
      <Stack alignItems="center"
        justifyContent="space-between"
        direction="row">
        {complete ? <CompleteIcon /> : <InCompleteIcon />}
        <Caption>
          {complete ? "Completed" : "In Progress"}
        </Caption>
      </Stack>
    </>
  );
}

export function AppearancesStack(props) {
  const { appearances = [] } = props;
  const collection = appearances.map((item) => item?.wrestler);

  return (
    <AvatarStack collection={collection} />
  );
}

export function ChampionshipStack({
  holders = 1,
  minPoints = null,
  maxPoints = null,
  wrestlers = [],
  brands = []
}) {
  const holderSt =
    wrestlers.length !== holders
      ? `${wrestlers.length} / ${holders} holders`
      : null;
  const pointsLabel = `${minPoints} to ${maxPoints} points`;
  return (
    <>
      <AvatarStack collection={brands.concat(wrestlers)} />
      {holderSt && (
        <Stack alignItems="center"
          justifyContent="space-between"
          direction="row">
          <FilterListIcon />
          <Caption>{holderSt}</Caption>
        </Stack>)}
      <Stack alignItems="center"
        justifyContent="space-between"
        direction="row">
        <NumbersIcon />
        <Caption>{pointsLabel}</Caption>
      </Stack>
    </>
  );
}

const GameButton = styled((props) => <Button variant="filled"
  data-qa={props?.['data-qa'] || props?.dataQa || ''}
  {...props} />)(() => ({
    textTransform: 'uppercase',
    fontSize: 12,
    letterSpacing: 1,
    color: theme => theme.palette.primary.light
  }))

export function WrestlerStack({
  id,
  brands = [],
  alignment = "NEUTRAL",
  cost = null,
  active = true,
  color = "",
  wins = 0,
  role = '',
  losses = 0,
  points = 50,
  // draws = 0,
  streak = 0
}) {
  const alignmentLabel = alignment === "GOOD" ? "Cheered" : alignment === "BAD" ? "Boo'd" : false
  const activeLabel = !active ? " - Retired" : ""
  return (
    <>
      {brands && <AvatarStack collection={brands} />}
      <GameButton size="small"
        data-qa={`wrestler-stack-${id}`}>{role}</GameButton>
      <Box sx={{ textAlign: "center" }}>
        {alignmentLabel}{activeLabel}
      </Box>
      <Caption sx={{ color, textAlign: "center" }}>
        {wins}W · {losses}L
        {streak > 0 && (
          <span>
            &nbsp;· {streak} streak
          </span>
        )}
      </Caption>
      <Box sx={{ textAlign: "center" }}>
        {numberFormatCurrency(cost)} · {points}pts
      </Box>
    </>
  );
}

export const Stacks = {
  [Groups.Brand]: BrandStack,
  [Groups.Championship]: ChampionshipStack,
  [Groups.Draft]: DraftStack,
  [Groups.Faction]: FactionStack,
  [Groups.Feud]: FeudStack,
  [Groups.Production]: ProductionStack,
  [Groups.Reign]: ReignStack,
  [Groups.Segment]: SegmentStack,
  [Groups.Wrestler]: WrestlerStack
};

export default Stacks;
